import {
  type SuppliersPageCustomCatalogsForVendorQuery,
  type SuppliersPageCustomCatalogsQuery,
} from '../../../../../generated';

export interface CustomCatalog {
  id: string;
  name: string;
}

export type CustomCatalogPageResult =
  | {
      kind: 'success';
      catalogs: CustomCatalog[];
      /** More catalogs match than fit in the page. */
      hasMore: boolean;
      /** Cursor to pass as the next page's start. */
      endCursor: string | null;
    }
  | { kind: 'error'; message: string };

const DEFAULT_ERROR_MESSAGE = 'Failed to load custom catalogs.';

/**
 * One page of a distributor's custom catalogs. `null` while there is no
 * response yet. Keeps the server's order, which the caller asks to be by name.
 */
export const mapCustomCatalogPage = (
  data: SuppliersPageCustomCatalogsForVendorQuery | undefined
): CustomCatalogPageResult | null => {
  const page = data?.customCatalogsForVendor;
  if (!page) return null;

  if (page.__typename !== 'CustomCatalogsForVendorResult') {
    return {
      kind: 'error',
      message:
        ('message' in page ? page.message : null) ?? DEFAULT_ERROR_MESSAGE,
    };
  }

  return {
    kind: 'success',
    catalogs: page.edges.map(({ node }) => ({ id: node.id, name: node.name })),
    hasMore: page.pageInfo.hasNextPage,
    endCursor: page.pageInfo.endCursor || null,
  };
};

/**
 * What looking one catalog up by id established. `not-found` only when the
 * backend answered cleanly without it; anything that stopped the answer —
 * network, session, a rejected id — is `failed`, because it proves nothing
 * about whether the catalog exists.
 */
export type CatalogLookup =
  | { status: 'loading' }
  | { status: 'found'; catalog: CustomCatalog }
  | { status: 'not-found' }
  | { status: 'failed' };

interface CatalogLookupResponse {
  data: SuppliersPageCustomCatalogsQuery | undefined;
  loading: boolean;
  /** A transport or GraphQL error reached the client. */
  hasError: boolean;
}

export const resolveCatalogLookup = (
  id: string,
  { data, loading, hasError }: CatalogLookupResponse
): CatalogLookup => {
  const result = data?.customCatalogs;
  const isResult = result?.__typename === 'CustomCatalogsResult';

  // Checked first: the client returns partial data alongside errors.
  const catalog = isResult
    ? result.customCatalogs.find((candidate) => candidate.id === id)
    : undefined;
  if (catalog) {
    return { status: 'found', catalog: { id: catalog.id, name: catalog.name } };
  }

  // Ahead of the failure checks: a retry still carries the previous error until
  // its own answer lands.
  if (loading) return { status: 'loading' };
  if (hasError || (result && !isResult)) return { status: 'failed' };
  if (isResult) return { status: 'not-found' };

  // No answer and no error: the request is about to go out.
  return { status: 'loading' };
};
